import { makeAutoObservable, observable, runInAction, toJS } from "mobx";
import { agent } from "../api/agent";
import { toast } from "react-toastify";
import { CompanySportVenueAsset, PartitionRule, PricingRule, DiscountRule } from "../models/CompanySportVenueAsset";
import { store } from "./store";
import i18n from "../../i18n";

export default class CompanySportVenueAssetStore {
    assetRegistry = new Map<string, CompanySportVenueAsset>();
    selectedAsset: CompanySportVenueAsset | undefined = undefined;
    editMode = false;
    loading = false;
    loadingInitial = false;
    predicate = new Map();
    
    constructor() {
        makeAutoObservable(this, {
            assetRegistry: observable,
        });
    }

    get assets() {
        return Array.from(this.assetRegistry.values());
    }

    get assetsByVenue() {
        const venueAssets = new Map<string, CompanySportVenueAsset[]>();
        
        this.assets.forEach(asset => {
            if (asset.companySportVenueID) {
                const venueId = asset.companySportVenueID;
                if (!venueAssets.has(venueId)) {
                    venueAssets.set(venueId, []);
                }
                venueAssets.get(venueId)!.push(asset);
            }
        });
        
        return venueAssets;
    }

    loadAllAssets = async () => {
        this.setLoadingInitial(true);
        try {
            const assets = await agent.CompanySportVenueAssets.list();
            runInAction(() => {
                this.assetRegistry.clear();
                assets.forEach(asset => {
                    this.setAsset(asset);
                });
                this.setLoadingInitial(false);
            });
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.setLoadingInitial(false);
            });
            toast.error((i18n as any).t('assets.errors.loadingAssets'));
        }
    };

    loadAsset = async (id: string) => {
        let asset = this.getAsset(id);
        if (asset) {
            this.selectedAsset = asset;
            return asset;
        } else {
            this.loadingInitial = true;
            try {
                asset = await agent.CompanySportVenueAssets.details(id);
                runInAction(() => {
                    this.setAsset(asset!);
                    this.selectedAsset = asset;
                    this.loadingInitial = false;
                });
                return asset;
            } catch (error) {
                console.log(error);
                runInAction(() => {
                    this.loadingInitial = false;
                });
                toast.error((i18n as any).t('assets.errors.loadingAssetDetails'));
            }
        }
    };

    createAsset = async (asset: CompanySportVenueAsset) => {
        this.loading = true;
        try {
            const plainAsset = toJS(asset);
            
            const assetForApi = {
                ...plainAsset,
                id: '00000000-0000-0000-0000-000000000000',
                // Clean up nested rules
                partitionRules: plainAsset.partitionRules?.map(rule => ({
                    capacityUnits: rule.capacityUnits,
                    isActive: rule.isActive,
                    companySportVenueAssetId: null,
                    id: null
                })) || [],
                pricingRules: plainAsset.pricingRules?.map(rule => ({
                    capacityUnits: rule.capacityUnits,
                    pricePerHour: rule.pricePerHour,
                    isActive: rule.isActive,
                    companySportVenueAssetId: null,
                    id: null
                })) || [],
                discountRules: plainAsset.discountRules?.map(rule => ({
                    ...rule,
                    companySportVenueAssetId: null,
                    id: null
                })) || []
            };
            
            
            const createdAsset = await agent.CompanySportVenueAssets.create(assetForApi);       
            runInAction(() => {
                this.setAsset(createdAsset);
                this.selectedAsset = createdAsset;
                this.editMode = false;
                this.loading = false;
            });
            
            const today = new Date();            
            await store.calendarStore.loadCalendarDataWithDateRange({
                startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
                endDate: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
            });
              
            toast.success((i18n as any).t('assets.success.assetCreated'));
            return createdAsset;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.creatingAsset'));
            return undefined;
        }
    };
    
    private setAsset = (asset: CompanySportVenueAsset) => {
        const observableAsset = observable({
            ...asset,
            sports: asset.sports ? [...asset.sports] : [],
            sportVenueAssetWorkingHours: asset.sportVenueAssetWorkingHours ? [...asset.sportVenueAssetWorkingHours] : [],
            partitionRules: asset.partitionRules ? [...asset.partitionRules] : [],
            pricingRules: asset.pricingRules ? [...asset.pricingRules] : [],
            discountRules: asset.discountRules ? [...asset.discountRules] : []
        });
        
        this.assetRegistry.set(asset.id, observableAsset);
    };

    updateAsset = async (asset: CompanySportVenueAsset) => {
        this.loading = true;
        try {
            const plainAsset = toJS(asset);
            
            // For updates, we keep the IDs but ensure proper structure
            const assetForApi = {
                ...plainAsset,
                partitionRules: plainAsset.partitionRules?.map(rule => ({
                    id: rule.id,
                    companySportVenueAssetId: asset.id, // Use the asset's ID for existing rules
                    capacityUnits: rule.capacityUnits,
                    isActive: rule.isActive
                })) || [],
                pricingRules: plainAsset.pricingRules?.map(rule => ({
                    id: rule.id,
                    companySportVenueAssetId: asset.id,
                    capacityUnits: rule.capacityUnits,
                    pricePerHour: rule.pricePerHour,
                    isActive: rule.isActive
                })) || [],
                discountRules: plainAsset.discountRules?.map(rule => ({
                    ...rule,
                    companySportVenueAssetId: asset.id,
                    id: rule.id
                })) || []
            };
            
            
            const updatedAsset = await agent.CompanySportVenueAssets.update(assetForApi);          
            runInAction(() => {
                this.assetRegistry.delete(asset.id);
                this.setAsset(updatedAsset);
                this.selectedAsset = updatedAsset;
                this.editMode = false;
                this.loading = false;
            });
            
            const today = new Date();
            await store.calendarStore.loadCalendarDataWithDateRange({
                startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
                endDate: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
            });
              
            toast.success((i18n as any).t('assets.success.assetUpdated'));
            return updatedAsset;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.updatingAsset'));
            return undefined;
        }
    };

    deleteAsset = async (id: string) => {
        this.loading = true;
        try {
            await agent.CompanySportVenueAssets.delete(id);
            runInAction(() => {
                this.assetRegistry.delete(id);
                this.loading = false;
            });
            
            // Reload calendar data
            const today = new Date();
            await store.calendarStore.loadCalendarDataWithDateRange({
                startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
                endDate: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
            });
              
            toast.success((i18n as any).t('assets.success.assetDeleted'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.deletingAsset'));
        }
    };

    // Partition Rules Management
    addPartitionRule = async (assetId: string, rule: Omit<PartitionRule, 'id'>) => {
        this.loading = true;
        try {
            const updatedAsset = await agent.CompanySportVenueAssets.addPartitionRule(assetId, rule);
            runInAction(() => {
                this.setAsset(updatedAsset);
                this.loading = false;
            });
            toast.success((i18n as any).t('assets.success.partitionRuleAdded'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.addingPartitionRule'));
        }
    };

    deletePartitionRule = async (assetId: string, ruleId: string) => {
        this.loading = true;
        try {
            const updatedAsset = await agent.CompanySportVenueAssets.deletePartitionRule(assetId, ruleId);
            runInAction(() => {
                this.setAsset(updatedAsset);
                this.loading = false;
            });
            toast.success((i18n as any).t('assets.success.partitionRuleDeleted'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.deletingPartitionRule'));
        }
    };

    // Pricing Rules Management
    addPricingRule = async (assetId: string, rule: Omit<PricingRule, 'id'>) => {
        this.loading = true;
        try {
            const updatedAsset = await agent.CompanySportVenueAssets.addPricingRule(assetId, rule);
            runInAction(() => {
                this.setAsset(updatedAsset);
                this.loading = false;
            });
            toast.success((i18n as any).t('assets.success.pricingRuleAdded'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.addingPricingRule'));
        }
    };

    updatePricingRule = async (assetId: string, rule: PricingRule) => {
        this.loading = true;
        try {
            const updatedAsset = await agent.CompanySportVenueAssets.updatePricingRule(assetId, rule);
            runInAction(() => {
                this.setAsset(updatedAsset);
                this.loading = false;
            });
            toast.success((i18n as any).t('assets.success.pricingRuleUpdated'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.updatingPricingRule'));
        }
    };

    deletePricingRule = async (assetId: string, ruleId: string) => {
        this.loading = true;
        try {
            const updatedAsset = await agent.CompanySportVenueAssets.deletePricingRule(assetId, ruleId);
            runInAction(() => {
                this.setAsset(updatedAsset);
                this.loading = false;
            });
            toast.success((i18n as any).t('assets.success.pricingRuleDeleted'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.deletingPricingRule'));
        }
    };

    // Discount Rules Management
    addDiscountRule = async (assetId: string, rule: Omit<DiscountRule, 'id'>) => {
        this.loading = true;
        try {
            const updatedAsset = await agent.CompanySportVenueAssets.addDiscountRule(assetId, rule);
            runInAction(() => {
                this.setAsset(updatedAsset);
                this.loading = false;
            });
            toast.success((i18n as any).t('assets.success.discountRuleAdded'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.addingDiscountRule'));
        }
    };

    updateDiscountRule = async (assetId: string, rule: DiscountRule) => {
        this.loading = true;
        try {
            const updatedAsset = await agent.CompanySportVenueAssets.updateDiscountRule(assetId, rule);
            runInAction(() => {
                this.setAsset(updatedAsset);
                this.loading = false;
            });
            toast.success((i18n as any).t('assets.success.discountRuleUpdated'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.updatingDiscountRule'));
        }
    };

    deleteDiscountRule = async (assetId: string, ruleId: string) => {
        this.loading = true;
        try {
            const updatedAsset = await agent.CompanySportVenueAssets.deleteDiscountRule(assetId, ruleId);
            runInAction(() => {
                this.setAsset(updatedAsset);
                this.loading = false;
            });
            toast.success((i18n as any).t('assets.success.discountRuleDeleted'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('assets.errors.deletingDiscountRule'));
        }
    };

    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    };

    clearSelectedAsset = () => {
        this.selectedAsset = undefined;
    };

    private getAsset = (id: string) => {
        return this.assetRegistry.get(id);
    };



    get axiosParams() {
        const params = new URLSearchParams();
        this.predicate.forEach((value, key) => {
            params.append(key, value.toString());
        });
        return params;
    }
}